import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Save, UserCircle2, Building2, Megaphone } from "lucide-react";

const ROLES = [
  { value: "entrepreneur", label: "Entrepreneur" },
  { value: "business_owner", label: "Business Owner" },
  { value: "investor", label: "Investor" },
  { value: "contractor", label: "Contractor" },
  { value: "influencer", label: "Influencer" },
];

const CATEGORIES = ["Fashion", "Beauty", "Tech", "Food", "Travel", "Fitness", "Gaming", "Business", "Lifestyle", "Education"];
const SOCIAL_PLATFORMS = ["instagram", "tiktok", "youtube", "twitter", "facebook"];

export default function Profile() {
  const { user, refresh, setRole } = useAuth();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [roleBusy, setRoleBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        company: user.company || "",
        bio: user.bio || "",
        location: user.location || "",
        company_logo: user.company_logo || "",
        company_website: user.company_website || "",
        company_industry: user.company_industry || "",
        company_description: user.company_description || "",
        social_links: user.social_links || {},
        follower_counts: user.follower_counts || {},
        content_categories: user.content_categories || [],
        portfolio_urls: (user.portfolio_urls || []).join("\n"),
      });
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        company: form.company,
        bio: form.bio,
        location: form.location,
      };
      if (user?.role === "business_owner") {
        payload.company_logo = form.company_logo;
        payload.company_website = form.company_website;
        payload.company_industry = form.company_industry;
        payload.company_description = form.company_description;
      }
      if (user?.role === "influencer") {
        payload.social_links = form.social_links;
        payload.follower_counts = Object.fromEntries(
          Object.entries(form.follower_counts || {}).map(([k, v]) => [k, Number(v) || 0])
        );
        payload.content_categories = form.content_categories;
        payload.portfolio_urls = (form.portfolio_urls || "")
          .split("\n").map((s) => s.trim()).filter(Boolean);
      }
      await api.put("/profile", payload);
      toast.success("Profile saved");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (role) => {
    if (role === user?.role) return;
    setRoleBusy(true);
    const res = await setRole(role);
    setRoleBusy(false);
    if (res.ok) toast.success(`Role updated to ${ROLES.find((r) => r.value === role)?.label}`);
    else toast.error(res.error);
  };

  const toggleCategory = (c) => {
    const current = form.content_categories || [];
    const next = current.includes(c) ? current.filter((x) => x !== c) : [...current, c];
    setForm({ ...form, content_categories: next });
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="profile-page">
      <div>
        <div className="tag"><span className="w-1.5 h-1.5 bg-[#C8102E]" /> Account</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3">Your profile</h1>
      </div>

      <div className="border border-zinc-200 bg-white p-6 flex items-center gap-4">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-14 h-14 object-cover" />
        ) : (
          <div className="w-14 h-14 bg-[#09090b] grid place-items-center text-white">
            <UserCircle2 className="w-7 h-7" />
          </div>
        )}
        <div className="min-w-0">
          <div className="font-display font-black text-xl tracking-tight truncate flex items-center gap-2">
            {user?.name || "—"}
            {user?.is_verified && (
              <span data-testid="profile-verified-badge" className="text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">
                Verified ✓
              </span>
            )}
          </div>
          <div className="text-sm text-zinc-500 truncate">{user?.email}</div>
        </div>
        {user?.is_admin && (
          <span className="ml-auto text-xs uppercase tracking-widest font-semibold border px-2 py-1 bg-blue-50 text-[#C8102E] border-blue-200">Admin</span>
        )}
      </div>

      <div className="border border-zinc-200 bg-white p-6" data-testid="role-section">
        <div className="overline text-zinc-500">Your role</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {ROLES.map((r) => (
            <button
              key={r.value}
              onClick={() => changeRole(r.value)}
              disabled={roleBusy}
              data-testid={`profile-role-${r.value}`}
              className={`text-xs uppercase tracking-widest font-semibold border px-3 py-1.5 transition-colors ${
                user?.role === r.value ? "bg-[#C8102E] text-white border-[#C8102E]" : "border-zinc-300 hover:bg-zinc-100"
              }`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      <form onSubmit={save} className="space-y-6" data-testid="profile-form">
        <div className="border border-zinc-200 bg-white p-6 space-y-4">
          <div className="overline text-zinc-500">Basics</div>
          <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="profile-name" />
          <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="profile-company" />
          <Field label="Location (city, state)" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="profile-location" />
          <div>
            <label className="overline text-zinc-500">Bio</label>
            <textarea value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="profile-bio" rows={3} className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]" />
          </div>
        </div>

        {user?.role === "business_owner" && (
          <div className="border border-zinc-200 bg-white p-6 space-y-4" data-testid="profile-business-section">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#C8102E]" />
              <div className="overline text-zinc-500">Company profile</div>
            </div>
            <Field label="Company logo URL" value={form.company_logo} onChange={(v) => setForm({ ...form, company_logo: v })} testid="profile-company-logo" />
            <Field label="Company website" value={form.company_website} onChange={(v) => setForm({ ...form, company_website: v })} testid="profile-company-website" />
            <Field label="Industry" value={form.company_industry} onChange={(v) => setForm({ ...form, company_industry: v })} testid="profile-company-industry" />
            <div>
              <label className="overline text-zinc-500">Company description</label>
              <textarea value={form.company_description || ""} onChange={(e) => setForm({ ...form, company_description: e.target.value })} data-testid="profile-company-description" rows={4} className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]" />
            </div>
          </div>
        )}

        {user?.role === "influencer" && (
          <div className="border border-zinc-200 bg-white p-6 space-y-5" data-testid="profile-influencer-section">
            <div className="flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-pink-600" />
              <div className="overline text-zinc-500">Creator profile</div>
            </div>

            <div>
              <label className="overline text-zinc-500">Content categories</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CATEGORIES.map((c) => {
                  const active = (form.content_categories || []).includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCategory(c)}
                      data-testid={`profile-category-${c.toLowerCase()}`}
                      className={`text-xs uppercase tracking-widest font-semibold border px-2 py-1 ${
                        active ? "bg-pink-500 text-white border-pink-500" : "border-zinc-300 hover:bg-zinc-100"
                      }`}
                    >{c}</button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="overline text-zinc-500">Social links & followers</label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SOCIAL_PLATFORMS.map((p) => (
                  <div key={p} className="border border-zinc-200 p-3">
                    <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{p}</div>
                    <input
                      placeholder={`https://${p}.com/yourhandle`}
                      value={(form.social_links || {})[p] || ""}
                      onChange={(e) => setForm({ ...form, social_links: { ...(form.social_links || {}), [p]: e.target.value } })}
                      data-testid={`profile-social-${p}`}
                      className="mt-2 w-full text-sm border border-zinc-200 px-2 py-1.5 outline-none focus:border-[#C8102E]"
                    />
                    <input
                      type="number"
                      placeholder="Follower count"
                      value={(form.follower_counts || {})[p] || ""}
                      onChange={(e) => setForm({ ...form, follower_counts: { ...(form.follower_counts || {}), [p]: e.target.value } })}
                      data-testid={`profile-followers-${p}`}
                      className="mt-2 w-full text-sm border border-zinc-200 px-2 py-1.5 outline-none focus:border-[#C8102E]"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="overline text-zinc-500">Portfolio links (one per line)</label>
              <textarea
                value={form.portfolio_urls || ""}
                onChange={(e) => setForm({ ...form, portfolio_urls: e.target.value })}
                data-testid="profile-portfolio"
                rows={4}
                className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E] font-mono text-sm"
                placeholder={"https://example.com/work1\nhttps://example.com/work2"}
              />
            </div>
          </div>
        )}

        <div>
          <button type="submit" disabled={busy} data-testid="profile-save" className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 font-semibold text-sm transition-colors">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="overline text-zinc-500">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]" />
    </div>
  );
}
